import React, {FC, useCallback, useEffect, useState} from "react";
import {
    Keyboard,
    KeyboardAvoidingView,
    Platform,
    Switch,
    Text,
    TouchableOpacity,
    TouchableWithoutFeedback,
    View
} from "react-native";
import {Style, styles} from "../../theme/styles";
import {Button, Gap, Spacer} from "../../components";
import {ButtonType, Frame, IsMobile} from "../../theme";
import useAuth from "../../contexts/Auth/useAuth";
import {NavigationProp, RouteProp, useNavigation, useRoute} from "@react-navigation/native";
import {AuthStackParams} from "../../navigation/Auth/AuthStackParams";
import {ForgotPasswordRoute, OrganizationAccessDeniedRoute, SignupRoute,} from "../../navigation/Auth/routes";
import TextInput from "../../components/TextInput";
import {useTheme} from "../../theme/colors";
import PageContainer from "../../components/PageContainer";
import CancelIcon from "../../Icons/CancelIcon";
import {Ionicons} from "@expo/vector-icons";
import * as Keychain from 'react-native-keychain'; // Import Keychain

enum ErrorVarients {
    EMAIL = "email",
    PASSWORD = "password",
    SERVER = "server",
}

type ErrorType = {
    type: ErrorVarients;
    message: string;
};

const Login: FC = () => {
    const [username, setUsername] = useState<string>(''); // Initialize with empty string
    const [password, setPassword] = useState<string>(''); // Initialize with empty string
    const [savePassword, setSavePassword] = useState<boolean>(false); // New state for the checkbox
    const [loading, setLoading] = useState<boolean>(false);
    const [isPasswordVisible, setPasswordVisibility] = useState(false);
    const {login, hasAccess, user} = useAuth();
    const [error, setError] = useState<ErrorType>();
    const navigation = useNavigation<NavigationProp<AuthStackParams>>();
    const route = useRoute<RouteProp<AuthStackParams, "Login">>();
    const theme = useTheme();

    useEffect(() => {
        const loadCredentials = async () => { // Function to load credentials
            try {
                const credentials = await Keychain.getGenericPassword(); // Get stored credentials
                if (credentials) {
                    setUsername(credentials.username); // Set username
                    setPassword(credentials.password); // Set password
                    setSavePassword(true);
                }
            } catch (err) {
                console.warn("Couldn't load credentials", err);
            }
        };

        loadCredentials(); // Call loadCredentials on component mount

        if (!hasAccess) {
            navigation.navigate(OrganizationAccessDeniedRoute);
        }
        setLoading(false);
    }, [hasAccess]);

    useEffect(() => {
        if (route?.params?.username) {
            setUsername(route.params.username);
        }
        if (route?.params?.password) {
            setPassword(route.params.password);
        }
    }, [route.params]);

    const handleSubmit = useCallback(async () => {


        setError(undefined);
        try {
            if (!username) {
                return setError({
                    type: ErrorVarients.SERVER,
                    message: "Please enter your email address",
                });
            }

            if (!password) {
                return setError({
                    type: ErrorVarients.SERVER,
                    message: "Please enter your password",
                });
            }

            setLoading(true);
            const response = await login(username, password);

            // ✅ Handle Cognito NEW_PASSWORD_REQUIRED challenge
            if (response?.challenge === "NEW_PASSWORD_REQUIRED") {
                navigation.navigate("ChangePassword", {user: response.user});
                setLoading(false);
                return;
            }

            // ✅ Save credentials if requested (for iOS only)
            if (Platform.OS === "ios" && savePassword) {
                await Keychain.setGenericPassword(username, password);
            } else if (Platform.OS === "ios" && !savePassword) {
                await Keychain.resetGenericPassword();
            }

        } catch (error: any) {
            setLoading(false);
            if (error.code === "UserNotConfirmedException") {
                return navigation.navigate(SignupRoute, {
                    verify: true,
                    email: username,
                });
            }

            setError({
                type: ErrorVarients.SERVER,
                message: "Invalid username or password",
            });
        } finally {
            // setLoading(false);
        }
    }, [username, password]);

    return (
      <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
          {Platform.OS !== "web" ? (
            <KeyboardAvoidingView
              behavior={Platform.OS === "ios" ? "padding" : "height"}
              style={{flex: 1}}
            >
                <PageContainer loading={loading}>
                    <View
                      style={[
                          styles.flex1,
                          {backgroundColor: theme.BACKGROUND},
                          !IsMobile && {width: Frame.width, alignSelf: "center"},
                      ]}
                    >
                        <Spacer/>
                        <View style={{...$container, backgroundColor: theme.BACKGROUND}}>
                            <Text
                              style={[
                                  styles.heading,
                                  styles.textCenter,
                                  {fontSize: 30, color: theme.TEXT},
                              ]}
                            >
                                Let's Log you in
                            </Text>
                            {error?.type === ErrorVarients.SERVER && (
                              <Text
                                style={[
                                    styles.textCenter,
                                    {fontSize: 15, marginTop: 10, color: theme.ERROR},
                                ]}
                              >
                                  {error?.message}
                              </Text>
                            )}
                            <View
                              style={[IsMobile ? styles.moileContainer : styles.desktopContainer]}
                            >
                                <TextInput
                                  keyboardType="email-address"
                                  autoCapitalize="none"
                                  autoCorrect={false}
                                  onChangeText={(text) => setUsername(text)}
                                  placeholder="username"
                                  error={error?.type === "email"}
                                  value={username}
                                  label={error?.type === "email" ? error.message : "Email"}
                                  style={[
                                      IsMobile ? styles.mobileinputs : styles.desktopinput,
                                      {backgroundColor: theme.INPUT_BACKGROUND || "#ffffff"},
                                  ]}
                                  textContentType={'username'}
                                  autoComplete="username"
                                />
                                <Gap gap={20}/>
                                <View style={{position: "relative"}}>
                                    <TextInput
                                      onChangeText={(text) => setPassword(text)}
                                      secureTextEntry={!isPasswordVisible}
                                      autoCorrect={false}
                                      placeholder="password"
                                      value={password}
                                      error={error?.type === "password"}
                                      label={error?.type === "password" ? error.message : "Password"}
                                      style={[
                                          IsMobile ? styles.mobileinputs : styles.desktopinput,
                                          {paddingRight: 50, backgroundColor: theme.INPUT_BACKGROUND || "#ffffff"},
                                      ]}
                                      textContentType={'password'}
                                      autoComplete="password"
                                    />
                                    {password && (
                                      <TouchableOpacity
                                        onPress={() => setPassword("")}
                                        style={{
                                            position: "absolute",
                                            right: 40,
                                            top: 25,
                                            zIndex: 1,
                                        }}
                                      >
                                          <CancelIcon height={16} width={16}/>
                                      </TouchableOpacity>
                                    )}
                                    <TouchableOpacity
                                      onPress={() => setPasswordVisibility(!isPasswordVisible)}
                                      style={{
                                          position: "absolute",
                                          right: 10,
                                          top: 22,
                                          zIndex: 1,
                                      }}
                                    >
                                        <Ionicons
                                          name={isPasswordVisible ? "eye-off" : "eye"}
                                          size={20}
                                        />
                                    </TouchableOpacity>
                                </View>
                                <Gap gap={20}/>
                                {Platform.OS === "ios" && (
                                  <View style={$savePasswordContainer}>
                                      <Switch
                                        trackColor={{false: "#767577", true: theme.ACCENT}}
                                        thumbColor={savePassword ? "#f4f3f4" : "#f4f3f4"}
                                        ios_backgroundColor="#3e3e3e"
                                        onValueChange={() => setSavePassword(previousState => !previousState)}
                                        value={savePassword}
                                      />
                                      <Text style={{color: theme.TEXT, marginLeft: 10}}>Save Password</Text>
                                  </View>
                                )}
                                <View
                                  style={[
                                      !IsMobile && {
                                          width: Frame.width / 3.8,
                                      },
                                  ]}
                                >
                                    <TouchableOpacity
                                      onPress={() => {
                                          navigation.navigate(ForgotPasswordRoute, {
                                              email: username,
                                          });
                                      }}
                                      style={[IsMobile ? $forgotPassword : $dforgotPassword]}
                                    >
                                        <Text
                                          style={{
                                              fontWeight: "600",
                                              color: theme.ACCENT,
                                              fontSize: 15,
                                          }}
                                        >
                                            Forgot Password?
                                        </Text>
                                    </TouchableOpacity>
                                </View>
                                <Gap gap={20}/>
                                <View style={styles.center}>
                                    <Button
                                      testID="login-submit"
                                      onPress={handleSubmit}
                                      title="Login"
                                      variant={ButtonType.secondary}
                                    />
                                </View>
                                <View
                                  style={[
                                      !IsMobile && {
                                          width: Frame.width / 3.8,
                                      },
                                  ]}
                                >
                                    <TouchableOpacity
                                      onPress={() => {
                                          navigation.navigate(SignupRoute, {});
                                      }}
                                      style={$createAccount}
                                    >
                                        <Text
                                          style={{
                                              fontWeight: "600",
                                              color: theme.ACCENT,
                                              fontSize: 15,
                                              textDecorationLine: "underline",
                                          }}
                                        >
                                            Create an account
                                        </Text>
                                    </TouchableOpacity>
                                </View>
                            </View>
                        </View>
                        <Spacer/>
                    </View>
                </PageContainer>
            </KeyboardAvoidingView>
          ) : (
            <PageContainer loading={loading}>
                <View
                  style={[
                      styles.flex1,
                      {backgroundColor: theme.BACKGROUND},
                      !IsMobile && {width: Frame.width / 2, alignSelf: "center"},
                  ]}
                >
                    <Spacer/>
                    <View style={{...$container, backgroundColor: theme.BACKGROUND}}>
                        <Text
                          style={[
                              styles.heading,
                              styles.textCenter,
                              {fontSize: 30, color: theme.TEXT},
                          ]}
                        >
                            Let's Log you in
                        </Text>
                        {error?.type === ErrorVarients.SERVER && (
                          <Text
                            style={[
                                styles.textCenter,
                                {fontSize: 15, marginTop: 10, color: theme.ERROR},
                            ]}
                          >
                              {error?.message}
                          </Text>
                        )}
                        <View
                          style={[IsMobile ? styles.moileContainer : styles.desktopContainer]}
                        >
                            <TextInput
                              keyboardType="email-address"
                              autoCapitalize="none"
                              autoCorrect={false}
                              onChangeText={(text) => setUsername(text)}
                              placeholder="username"
                              error={error?.type === "email"}
                              value={username}
                              label={error?.type === "email" ? error.message : "Email"}
                              style={[
                                  IsMobile ? styles.mobileinputs : styles.desktopinput,
                                  {backgroundColor: theme.INPUT_BACKGROUND || "#ffffff"},
                              ]}
                              textContentType={'username'}
                              autoComplete="username"
                            />
                            <Gap gap={20}/>
                            <View style={{position: "relative"}}>
                                <TextInput
                                  onChangeText={(text) => setPassword(text)}
                                  secureTextEntry={!isPasswordVisible}
                                  autoCorrect={false}
                                  placeholder="password"
                                  value={password}
                                  error={error?.type === "password"}
                                  label={error?.type === "password" ? error.message : "Password"}
                                  style={[
                                      IsMobile ? styles.mobileinputs : styles.desktopinput,
                                      {paddingRight: 50, backgroundColor: theme.INPUT_BACKGROUND || "#ffffff"},
                                  ]}
                                  textContentType={'password'}
                                  autoComplete="password"
                                />
                                {password && (
                                  <TouchableOpacity
                                    onPress={() => setPassword("")}
                                    style={{
                                        position: "absolute",
                                        right: 40,
                                        top: 25,
                                        zIndex: 1,
                                    }}
                                  >
                                      <CancelIcon height={16} width={16}/>
                                  </TouchableOpacity>
                                )}
                                <TouchableOpacity
                                  onPress={() => setPasswordVisibility(!isPasswordVisible)}
                                  style={{
                                      position: "absolute",
                                      right: 10,
                                      top: 22,
                                      zIndex: 1,
                                  }}
                                >
                                    <Ionicons
                                      name={isPasswordVisible ? "eye-off" : "eye"}
                                      size={20}
                                    />
                                </TouchableOpacity>
                            </View>
                            <Gap gap={20}/>
                            <View
                              style={[
                                  !IsMobile && {
                                      width: Frame.width / 3.8,
                                  },
                              ]}
                            >
                                <TouchableOpacity
                                  onPress={() => {
                                      navigation.navigate(ForgotPasswordRoute, {
                                          email: username,
                                      });
                                  }}
                                  style={[IsMobile ? $forgotPassword : $dforgotPassword]}
                                >
                                    <Text
                                      style={{
                                          fontWeight: "600",
                                          color: theme.ACCENT,
                                          fontSize: 15,
                                      }}
                                    >
                                        Forgot Password?
                                    </Text>
                                </TouchableOpacity>
                            </View>
                            <Gap gap={20}/>
                            <View style={styles.center}>
                                <Button
                                  testID="login-submit"
                                  onPress={handleSubmit}
                                  title="Login"
                                  variant={ButtonType.secondary}
                                />
                            </View>
                            <View
                              style={[
                                  !IsMobile && {
                                      width: Frame.width / 3.8,
                                  },
                              ]}
                            >
                                <TouchableOpacity
                                  onPress={() => {
                                      navigation.navigate(SignupRoute, {});
                                  }}
                                  style={$createAccount}
                                >
                                    <Text
                                      style={{
                                          fontWeight: "600",
                                          color: theme.ACCENT,
                                          fontSize: 15,
                                          textDecorationLine: "underline",
                                      }}
                                    >
                                        Create an account
                                    </Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>
                    <Spacer/>
                </View>
            </PageContainer>
          )}
      </TouchableWithoutFeedback>
    );

};

export default Login;

const $container: Style = {
    flex: 3,
    padding: 20,
};
const $forgotPassword: Style = {
    backgroundColor: "transparent",
    alignSelf: "flex-end",
    marginVertical: 5,
};
const $dforgotPassword: Style = {
    alignSelf: "flex-end",
    marginVertical: 20,
};
const $createAccount: Style = {
    alignSelf: "center",
    marginVertical: 20,
};
const $savePasswordContainer: Style = {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
    justifyContent: "flex-end", // Align to the right
};